// src/components/games/BallDrop.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Diamond, Coins, Crown, Target, Plus, Minus, XCircle, ArrowLeft } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

const BASE_WIDTH = 1400; 
const BALL_SIZE = 60; 
const PLATFORM_H = 60; 
const BALL_START_Y = 140; 
const ENTRY_FEE = 10;

const HOLES = [
  { id: 'x2', start: 200, width: 160, mult: 2, color: '#0ea5e9', label: 'Sapphire', border: '#0369a1', shadow: '#082f49' },
  { id: 'x10', start: 650, width: 80, mult: 10, color: '#fbbf24', label: 'Gold', border: '#b45309', shadow: '#713f12' },
  { id: 'x3', start: 1050, width: 140, mult: 3, color: '#e11d48', label: 'Ruby', border: '#9f1239', shadow: '#4c0519' },
];

interface BallDropProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: any;
  updateProfile: (data: any) => Promise<void>;
  onBuyCredits: () => void;
}

export const HypeHighRoller: React.FC<BallDropProps> = ({ 
  isOpen, 
  onClose, 
  currentUser, 
  updateProfile,
  onBuyCredits 
}) => {
  const [dimensions, setDimensions] = useState({ w: window.innerWidth, h: window.innerHeight });
  
  // Game State
  const [credits, setCredits] = useState(0);
  const [wager, setWager] = useState(10);
  const [level, setLevel] = useState(1);
  const [gameState, setGameState] = useState('betting');
  const [lastResult, setLastResult] = useState<any>(null);
  const [totalWon, setTotalWon] = useState(0);

  // Physics Refs
  const platformXRef = useRef(0);
  const platformSpeedRef = useRef(2.5); 
  const ballVelocityRef = useRef(0);
  const ballYRef = useRef(BALL_START_Y);
  const fallingThroughHoleRef = useRef<any>(null);
  
  const platformElementRef = useRef<HTMLDivElement>(null);
  const ballElementRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<any>(null);

  // 🔥 Load credits from props
  useEffect(() => {
    if (currentUser?.cred !== undefined) {
      setCredits(currentUser.cred);
    }
  }, [currentUser]);

  // 🔥 Update CRD helper - only call when needed, not in animation loop
  const updateCredits = async (amount: number) => {
    if (!currentUser?.uid) return;
    const newBalance = credits + amount;
    setCredits(newBalance);
    await updateProfile({ cred: newBalance });
  };

  // Handle resize
  useEffect(() => {
    const handleResize = () => setDimensions({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update speed based on level
  useEffect(() => {
    platformSpeedRef.current = 2.0 + (level * 0.8);
  }, [level]);

  // 🔥 RESOLVE DROP - Fixed to work properly
  const resolveDrop = useCallback(async (won: boolean, hole: any) => {
    if (won && hole) {
      // WIN: Add winnings, increase level, reset ball
      const winnings = wager * hole.mult;
      const newTotal = totalWon + winnings;
      setTotalWon(newTotal);
      setLastResult({ won: true, amount: winnings, mult: hole.mult, color: hole.color });
      setLevel((prev) => prev + 1); 
      
      resetBall();
      setGameState('ready');
      
      setTimeout(() => {
        setLastResult((prev) => (prev?.won ? null : prev));
      }, 1000);

    } else {
      // LOSS: Award total winnings if any, reset everything
      if (totalWon > 0) {
        await updateCredits(totalWon);
        
        try {
          await addDoc(collection(db, "notifications"), {
            userId: currentUser.uid,
            type: 'reward',
            title: 'BALL DROP WIN! 🎯',
            message: `You won ${totalWon} CRD in Ball Drop! Streak: ${level}`,
            read: false,
            createdAt: serverTimestamp()
          });
        } catch (e) {
          console.error("Failed to save notification:", e);
        }
      }
      
      setLastResult({ won: false, amount: wager, mult: 0 });
      setLevel(1);
      setGameState('result');
      setTotalWon(0);
      
      setTimeout(() => {
        resetBall();
        setGameState('betting');
        setLastResult(null);
      }, 2000); 
    }
  }, [wager, totalWon, level, currentUser]);

  // 🔥 PHYSICS UPDATE - Calls resolveDrop but doesn't await it
  const updatePhysics = useCallback(() => {
    // Move platform
    platformXRef.current -= platformSpeedRef.current;
    
    if (platformXRef.current <= -BASE_WIDTH) {
      platformXRef.current += BASE_WIDTH;
    }

    if (platformElementRef.current) {
      platformElementRef.current.style.transform = `translateX(${platformXRef.current}px)`;
    }

    // Handle ball falling
    if (gameState === 'dropping') {
      ballVelocityRef.current += 0.4;
      ballYRef.current += ballVelocityRef.current;
      
      const platformYOffset = dimensions.h - 180;

      // Collision with platform
      if (ballYRef.current + BALL_SIZE >= platformYOffset && ballYRef.current <= platformYOffset + PLATFORM_H) {
        
        if (!fallingThroughHoleRef.current) {
          const ballCenterX = dimensions.w / 2;
          
          let relativeX = (ballCenterX - platformXRef.current) % BASE_WIDTH;
          if (relativeX < 0) relativeX += BASE_WIDTH;

          let foundHole = null;
          for (let i = 0; i < HOLES.length; i++) {
            const hole = HOLES[i];
            if (relativeX >= hole.start + (BALL_SIZE / 2) && relativeX <= hole.start + hole.width - (BALL_SIZE / 2)) {
              foundHole = hole;
              break;
            }
          }

          if (foundHole) {
            fallingThroughHoleRef.current = foundHole;
          } else {
            ballYRef.current = platformYOffset - BALL_SIZE;
            resolveDrop(false, null);
            if (ballElementRef.current) {
              ballElementRef.current.style.transform = `translateY(${ballYRef.current}px)`;
            }
            return; 
          }
        }
      }

      // Fell off screen
      if (ballYRef.current > dimensions.h + BALL_SIZE) {
        const hole = fallingThroughHoleRef.current;
        fallingThroughHoleRef.current = null;
        resolveDrop(true, hole);
        return; 
      }

      if (ballElementRef.current) {
        ballElementRef.current.style.transform = `translateY(${ballYRef.current}px)`;
      }
    }

    requestRef.current = requestAnimationFrame(updatePhysics);
  }, [gameState, dimensions, resolveDrop]);

  // Start animation loop
  useEffect(() => {
    requestRef.current = requestAnimationFrame(updatePhysics);
    return () => cancelAnimationFrame(requestRef.current);
  }, [updatePhysics]);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && gameState === 'ready') {
        e.preventDefault();
        triggerDrop();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState]);

  const triggerDrop = () => {
    if (gameState !== 'ready') return;
    setGameState('dropping');
    ballVelocityRef.current = 0;
    fallingThroughHoleRef.current = null;
  };

  const resetBall = () => {
    ballYRef.current = BALL_START_Y;
    ballVelocityRef.current = 0;
    fallingThroughHoleRef.current = null;
    if (ballElementRef.current) {
      ballElementRef.current.style.transform = `translateY(${BALL_START_Y}px)`;
    }
  };

  const placeWager = async () => {
    if (wager > credits || wager <= 0) return;
    if (credits < ENTRY_FEE) {
      setLastResult({ won: false, amount: 0, mult: 0, error: `Need ${ENTRY_FEE} CRD to play` });
      setTimeout(() => setLastResult(null), 2000);
      return;
    }
    
    await updateCredits(-wager);
    setGameState('ready');
  };

  const renderPattern = (keyPrefix: string) => {
    const segments = [];
    let currentX = 0;

    HOLES.forEach((hole, index) => {
      if (hole.start > currentX) {
        segments.push(
          <div 
            key={`${keyPrefix}-solid-${index}`} 
            style={{ width: `${hole.start - currentX}px` }} 
            className="h-full relative overflow-visible z-10"
          >
             <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-black border-t-[6px] border-t-yellow-600 border-b-[8px] border-b-black shadow-[0_15px_30px_rgba(0,0,0,0.8)]">
                <div className="absolute top-0 left-0 w-full h-1 bg-white/20"></div>
             </div>
          </div>
        );
      }
      
      segments.push(
        <div 
          key={`${keyPrefix}-hole-${hole.id}`} 
          style={{ width: `${hole.width}px` }}
          className="h-full relative overflow-visible flex flex-col items-center justify-start group"
        >
          <div 
            className="absolute -top-14 px-5 py-2 rounded-lg border-2 text-2xl flex items-center gap-2 shadow-2xl z-0 bg-black/90 backdrop-blur-sm"
            style={{ 
              borderColor: hole.color, 
              color: hole.color,
              boxShadow: `0 0 20px ${hole.color}60, inset 0 0 10px ${hole.color}40`
            }}
          >
            <Diamond size={20} fill="currentColor" /> <span className="font-bold">x{hole.mult}</span>
          </div>
          
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-white/50 to-transparent"></div>
          <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-white/50 to-transparent"></div>
        </div>
      );
      
      currentX = hole.start + hole.width;
    });

    if (currentX < BASE_WIDTH) {
      segments.push(
        <div 
          key={`${keyPrefix}-solid-end`} 
          style={{ width: `${BASE_WIDTH - currentX}px` }} 
          className="h-full relative overflow-visible z-10"
        >
           <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-black border-t-[6px] border-t-yellow-600 border-b-[8px] border-b-black shadow-[0_15px_30px_rgba(0,0,0,0.8)]">
              <div className="absolute top-0 left-0 w-full h-1 bg-white/20"></div>
           </div>
        </div>
      );
    }
    return segments;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[500] bg-black overflow-hidden select-none font-sans">
      
      {/* TOP BAR */}
      <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-3 sm:px-4 py-3 bg-black/80 backdrop-blur-xl border-b border-red-500/20">
        <button
          onClick={onClose}
          className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-black/60 border border-red-500/30 rounded-xl text-white hover:bg-red-500/20 hover:border-red-400 transition-all duration-300 group"
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform duration-300" />
          <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest">Back</span>
        </button>

        <div className="flex items-center gap-2 bg-black/60 border border-red-500/30 rounded-xl px-3 sm:px-4 py-2">
          <Coins size={16} className="text-yellow-400" />
          <span className="text-white font-bold font-mono text-xs sm:text-sm">{credits} CRD</span>
        </div>
      </div>

      <div className="w-full h-full" onClick={() => { if(gameState === 'ready') triggerDrop(); }}>
        {/* Background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,_#1e1b4b,_#000000)] pointer-events-none">
           <div className="absolute top-[-10%] left-[10%] w-[600px] h-[600px] bg-yellow-600/5 rounded-full blur-[150px]"></div>
           <div className="absolute top-[20%] right-[10%] w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[120px]"></div>
        </div>

        {/* Header */}
        <div className="absolute top-16 left-0 w-full px-4 md:px-12 z-40 pointer-events-none flex items-start justify-between">
          <div className="flex items-center gap-3 text-2xl md:text-3xl text-yellow-500 bg-black/60 border border-yellow-600/50 backdrop-blur-md px-6 py-3 rounded-lg shadow-[0_0_15px_rgba(202,138,4,0.1)]">
            <Diamond className="text-yellow-400" size={28} />
            <span className="font-light tracking-wider">{totalWon} CRD</span>
          </div>

          <div className="flex items-center gap-3 text-2xl md:text-3xl text-gray-200 bg-black/60 border border-gray-600/50 backdrop-blur-md px-6 py-3 rounded-lg shadow-[0_0_15px_rgba(255,255,255,0.05)]">
            <Crown className="text-gray-400" size={28} />
            <span className="font-light tracking-wider">STREAK <span className="font-bold text-white">{level}</span></span>
          </div>
        </div>

        {/* Ball */}
        <div 
          ref={ballElementRef}
          className="absolute z-20 rounded-full will-change-transform border-[3px] border-slate-300"
          style={{ 
            width: `${BALL_SIZE}px`, 
            height: `${BALL_SIZE}px`,
            left: `${(dimensions.w / 2) - (BALL_SIZE / 2)}px`,
            top: 0, 
            transform: `translateY(${BALL_START_Y}px)`,
            background: 'radial-gradient(circle at 35% 35%, #ffffff, #94a3b8 40%, #1e293b)',
            boxShadow: '0 20px 30px rgba(0,0,0,0.8), inset -5px -5px 15px rgba(0,0,0,0.6)'
          }}
        />

        {/* Platform */}
        <div 
          ref={platformElementRef}
          className="absolute flex will-change-transform"
          style={{ 
            width: `${BASE_WIDTH * 2}px`, 
            height: `${PLATFORM_H}px`,
            top: `${dimensions.h - 180}px`,
            left: '0',
          }}
        >
          {renderPattern('set1')}
          {renderPattern('set2')}
        </div>

        {/* Wager Menu */}
        {gameState === 'betting' && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-50 flex flex-col items-center justify-center p-6 animate-in zoom-in-95 duration-500">
            <div className="bg-zinc-950 border border-yellow-600/30 p-10 rounded-2xl shadow-[0_0_50px_rgba(202,138,4,0.1)] w-full max-w-[450px] text-center relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent opacity-50"></div>

              <div className="mb-8">
                 <Diamond className="text-yellow-500 mx-auto mb-4" size={48} />
                 <h2 className="text-gray-200 text-2xl font-light tracking-[0.2em] uppercase">Set Wager</h2>
                 <p className="text-zinc-500 text-xs mt-2">Entry Fee: {ENTRY_FEE} CRD</p>
              </div>
              
              <div className="flex items-center justify-between bg-black/50 rounded-xl p-4 mb-8 border border-gray-800">
                <button 
                  onClick={(e) => { e.stopPropagation(); setWager(Math.max(ENTRY_FEE, wager - 10)); }}
                  className="w-14 h-14 flex items-center justify-center bg-zinc-900 text-gray-300 rounded-full border-[4px] border-dashed border-gray-600 hover:border-gray-400 hover:text-white transition-all active:scale-95"
                ><Minus size={24} strokeWidth={3} /></button>
                
                <div className="text-5xl text-yellow-500 font-light tracking-widest">{wager} CRD</div>

                <button 
                  onClick={(e) => { e.stopPropagation(); setWager(Math.min(credits, wager + 10)); }}
                  className="w-14 h-14 flex items-center justify-center bg-zinc-900 text-gray-300 rounded-full border-[4px] border-dashed border-gray-600 hover:border-gray-400 hover:text-white transition-all active:scale-95"
                ><Plus size={24} strokeWidth={3} /></button>
              </div>

              {credits < ENTRY_FEE && (
                <button
                  onClick={onBuyCredits}
                  className="text-xs text-yellow-400 hover:text-yellow-300 underline underline-offset-2 font-bold uppercase tracking-widest mb-4 block"
                >
                  Buy More CRD →
                </button>
              )}

              <button 
                onClick={(e) => { e.stopPropagation(); placeWager(); }}
                disabled={wager > credits || wager < ENTRY_FEE}
                className="w-full py-4 bg-gradient-to-r from-yellow-700 via-yellow-500 to-yellow-700 text-black font-bold text-xl uppercase tracking-widest rounded-lg hover:shadow-[0_0_20px_rgba(234,179,8,0.4)] transition-all active:scale-[0.98] disabled:opacity-50 disabled:grayscale"
              >
                {credits < ENTRY_FEE ? 'Need CRD' : 'Confirm Bet'}
              </button>
            </div>
          </div>
        )}

        {/* Target Prompt */}
        {gameState === 'ready' && (
          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
            <div className="bg-black/40 backdrop-blur-sm px-8 py-3 rounded-full border border-gray-500/30 mt-[300px] animate-pulse">
              <p className="text-gray-300 text-xl font-light tracking-[0.3em] flex items-center gap-4">
                <Target size={20} className="text-yellow-500" /> TAP TO DROP <Target size={20} className="text-yellow-500" />
              </p>
            </div>
          </div>
        )}

        {/* Win Animation */}
        {lastResult?.won && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 pointer-events-none animate-in slide-in-from-bottom-8 fade-in duration-300 zoom-in-110">
            <div className="text-center flex flex-col items-center">
              <p className="text-7xl font-bold mb-4 drop-shadow-[0_0_20px_currentColor]" style={{ color: lastResult.color }}>
                +{lastResult.amount} CRD
              </p>
              <p className="text-black bg-white px-6 py-2 rounded-full font-bold tracking-widest text-lg uppercase shadow-[0_0_15px_rgba(255,255,255,0.5)]">
                Streak +1
              </p>
            </div>
          </div>
        )}

        {/* Loss Overlay */}
        {gameState === 'result' && lastResult && !lastResult.won && (
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md z-50 flex flex-col items-center justify-center animate-in fade-in duration-500">
            <div className="text-center">
              <XCircle className="text-red-600 mx-auto mb-6 drop-shadow-[0_0_15px_rgba(220,38,38,0.6)]" size={80} />
              <p className="text-red-500 text-5xl font-light tracking-[0.3em] uppercase mb-4 drop-shadow-[0_0_10px_rgba(220,38,38,0.4)]">Bust</p>
              <p className="text-2xl text-gray-400 font-light tracking-widest">Lost {lastResult.amount} CRD</p>
              {totalWon > 0 && (
                <p className="text-green-400 text-xl mt-4">Kept {totalWon} CRD winnings!</p>
              )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {lastResult?.error && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-red-500/90 backdrop-blur-md text-white px-8 py-4 rounded-xl border border-red-400 shadow-[0_0_30px_rgba(216,0,0,0.5)] animate-in fade-in duration-300">
            <p className="text-xl font-bold">{lastResult.error}</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default HypeHighRoller;